import React, { useCallback, useMemo } from "react";

import { Box, useTheme } from "@mui/material";
import type { NodeMouseHandler, Node, Edge } from "@xyflow/react";
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  type NodeOrigin,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import type { PipelineNodeData } from "src/domains/application/hooks/usePipelineGraph";

import { PipelineCustomNode } from "./PipelineCustomNode";
import { PipelineGroupNode } from "./PipelineGroupNode";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PipelineDAGViewProps {
  nodes: Node[];
  edges: Edge[];
  onNodeClick: (nodeId: string, data: PipelineNodeData) => void;
  /** Dim nodes that don't match the active filter */
  filteredNodeIds?: Set<string>;
  /** Service count for dynamic container height */
  serviceCount: number;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const NODE_ORIGIN: NodeOrigin = [0, 0.5];

// ── Component ─────────────────────────────────────────────────────────────────

export function PipelineDAGView({
  nodes,
  edges,
  onNodeClick,
  filteredNodeIds,
  serviceCount,
}: PipelineDAGViewProps): React.ReactElement {
  const theme = useTheme();

  const nodeTypes = useMemo(
    () => ({
      pipeline: PipelineCustomNode,
      "pipeline-group": PipelineGroupNode,
    }),
    [],
  );

  // Apply opacity to filtered-out nodes
  const styledNodes = useMemo(() => {
    if (!filteredNodeIds || filteredNodeIds.size === 0) return nodes;
    return nodes.map((n) => ({
      ...n,
      style: {
        ...n.style,
        opacity: filteredNodeIds.has(n.id) ? 1 : 0.25,
        transition: "opacity 0.3s",
      },
    }));
  }, [nodes, filteredNodeIds]);

  const styledEdges = useMemo(() => {
    if (!filteredNodeIds || filteredNodeIds.size === 0) return edges;
    return edges.map((e) => {
      const sourceVisible = filteredNodeIds.has(e.source);
      const targetVisible = filteredNodeIds.has(e.target);
      return {
        ...e,
        style: {
          ...e.style,
          opacity: sourceVisible && targetVisible ? 1 : 0.15,
          transition: "opacity 0.3s",
        },
      };
    });
  }, [edges, filteredNodeIds]);

  const handleNodeClick: NodeMouseHandler = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      onNodeClick(node.id, node.data as PipelineNodeData);
    },
    [onNodeClick],
  );

  // Container fills the full-width main area (master-detail layout).
  // Generous minHeight keeps the DAG prominent and immediately visible.
  const containerHeight = "100%";

  return (
    <Box
      sx={{
        width: "100%",
        height: containerHeight,
        minHeight: 480,
        // React Flow requires explicit dimensions — ensure they're always set
        // before the graph renders to avoid the "needs width and height" warning.
        "& .react-flow": {
          width: "100%",
          height: "100%",
        },
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        overflow: "hidden",
        "& .react-flow__node": {
          cursor: "pointer",
          // Smooth position transitions when dagre relayouts on state changes
          transition: "transform 0.3s ease, opacity 0.3s ease",
        },
        "& .react-flow__edge-path": {
          strokeWidth: 2,
          // Smooth edge transitions on layout changes
          transition: "d 0.3s ease, stroke 0.3s ease, opacity 0.3s ease",
        },
        "& .react-flow__controls": {
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          boxShadow: 1,
        },
        "& .react-flow__controls-button": {
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <ReactFlow
        nodes={styledNodes}
        edges={styledEdges}
        nodeTypes={nodeTypes}
        nodeOrigin={NODE_ORIGIN}
        defaultEdgeOptions={{
          type: "default",
          style: { strokeWidth: 2, stroke: theme.palette.grey[300] },
        }}
        onNodeClick={handleNodeClick}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        proOptions={{ hideAttribution: true }}
        minZoom={0.3}
        maxZoom={1.5}
        panOnDrag
        zoomOnScroll
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color={theme.palette.divider}
        />
        <Controls showInteractive={false} />
        {serviceCount >= 3 && (
          <MiniMap
            maskColor="rgba(0, 0, 0, 0.5)"
            nodeColor={(node) => {
              const status = (node.data as PipelineNodeData)?.status;
              if (status === "completed") return theme.palette.success.main;
              if (status === "in_progress") return theme.palette.info.main;
              if (status === "failed") return theme.palette.error.main;
              return theme.palette.grey[400];
            }}
            style={{
              width: 120,
              height: 80,
            }}
          />
        )}
      </ReactFlow>
    </Box>
  );
}
